import React, { useState, useCallback } from 'react';
import { Card } from '@playground/ui';
import { apiFetch } from '@/platform/api.js';

interface GgrRow {
  day?: string;
  game_id?: string;
  player_id?: string;
  display_name?: string;
  turnover: number;
  winnings: number;
  ggr: number;
  bet_count: number;
  player_count: number;
}

interface GgrResponse {
  rows: GgrRow[];
  fromDate: string;
  toDate: string;
  groupBy: string[];
}

const GROUP_OPTIONS = [
  { id: 'day',    label: 'Day' },
  { id: 'game',   label: 'Game' },
  { id: 'player', label: 'Player' },
];

/**
 * Formats a Date as YYYY-MM-DD in the *local* calendar.
 *
 * `toISOString()` converts to UTC first, so for anyone west of UTC an evening
 * "today" rendered as tomorrow's date.
 */
function toDateInput(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Renders a `YYYY-MM-DD` day bucket as a local-calendar date.
 *
 * `new Date('2026-07-09')` parses as UTC midnight, which in a negative-offset
 * timezone displays as the previous day.
 */
function formatDayBucket(day: string): string {
  const parts = day.slice(0, 10).split('-').map(Number);
  const [y, m, d] = parts;
  if (y === undefined || m === undefined || d === undefined || Number.isNaN(y)) {
    return day;
  }
  return new Date(y, m - 1, d).toLocaleDateString();
}

function formatNum(n: number | null | undefined): string {
  if (n == null) return '—';
  return Number(n).toLocaleString();
}

function ggrColor(ggr: number): string {
  if (ggr > 0) return 'text-green-600 dark:text-green-400';
  if (ggr < 0) return 'text-red-600 dark:text-red-400';
  return 'text-gray-500 dark:text-gray-400';
}

export function GgrStats({ token }: { token: string }) {
  const [from, setFrom] = useState(toDateInput(new Date(Date.now() - 7 * 86400000)));
  const [to, setTo] = useState(toDateInput(new Date()));
  const [groups, setGroups] = useState<Set<string>>(new Set(['day']));
  const [data, setData] = useState<GgrResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const toggleGroup = (id: string) => {
    setGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const groupBy = [...groups].join(',') || 'day';
      const res = await apiFetch(
        `/api/admin/stats/ggr?from=${from}&to=${to}T23:59:59&groupBy=${groupBy}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json() as GgrResponse;
      setData(json);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  }, [from, to, groups, token]);

  // Summary totals
  const totals = data?.rows.reduce(
    (acc, r) => ({
      turnover: acc.turnover + Number(r.turnover),
      winnings: acc.winnings + Number(r.winnings),
      ggr: acc.ggr + Number(r.ggr),
      bet_count: acc.bet_count + Number(r.bet_count),
    }),
    { turnover: 0, winnings: 0, ggr: 0, bet_count: 0 },
  );

  const showDay     = groups.has('day');
  const showGame    = groups.has('game');
  const showPlayer  = groups.has('player');

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex flex-col gap-1">
            <label htmlFor="ggr-from" className="text-xs font-semibold text-gray-500 dark:text-gray-400">From</label>
            <input
              id="ggr-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-800 dark:text-gray-100"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="ggr-to" className="text-xs font-semibold text-gray-500 dark:text-gray-400">To</label>
            <input
              id="ggr-to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-800 dark:text-gray-100"
            />
          </div>
          <div className="flex flex-col gap-1">
            <p id="ggr-groupby-label" className="text-xs font-semibold text-gray-500 dark:text-gray-400">Group by</p>
            <div role="group" aria-labelledby="ggr-groupby-label" className="flex gap-2">
              {GROUP_OPTIONS.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => toggleGroup(g.id)}
                  aria-pressed={groups.has(g.id)}
                  className={[
                    'px-3 py-2 rounded-lg border text-sm font-medium transition-colors',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2',
                    groups.has(g.id)
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800',
                  ].join(' ')}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={() => void fetchStats()}
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? 'Loading…' : 'Load'}
          </button>
        </div>
        {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
      </Card>

      {/* Summary cards */}
      {totals && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Turnover', value: formatNum(totals.turnover), sub: 'total credits bet' },
            { label: 'Winnings', value: formatNum(totals.winnings), sub: 'total credits paid out' },
            { label: 'GGR', value: formatNum(totals.ggr), sub: 'turnover − winnings', highlight: true, raw: totals.ggr },
            { label: 'Bets', value: formatNum(totals.bet_count), sub: 'total wagers placed' },
          ].map((s) => (
            <Card key={s.label}>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{s.label}</p>
              <p className={`text-2xl font-black mt-1 ${s.raw !== undefined ? ggrColor(s.raw) : 'text-gray-800 dark:text-gray-100'}`}>
                {s.value}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{s.sub}</p>
            </Card>
          ))}
        </div>
      )}

      {/* Table */}
      {data && data.rows.length > 0 && (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700 text-left">
                  {showDay    && <th scope="col" className="pb-2 pr-4 text-xs font-semibold text-gray-500 dark:text-gray-400">Date</th>}
                  {showGame   && <th scope="col" className="pb-2 pr-4 text-xs font-semibold text-gray-500 dark:text-gray-400">Game</th>}
                  {showPlayer && <th scope="col" className="pb-2 pr-4 text-xs font-semibold text-gray-500 dark:text-gray-400">Player</th>}
                  <th scope="col" className="pb-2 pr-4 text-xs font-semibold text-gray-500 dark:text-gray-400 text-right">Turnover</th>
                  <th scope="col" className="pb-2 pr-4 text-xs font-semibold text-gray-500 dark:text-gray-400 text-right">Winnings</th>
                  <th scope="col" className="pb-2 pr-4 text-xs font-semibold text-gray-500 dark:text-gray-400 text-right">GGR</th>
                  <th scope="col" className="pb-2 text-xs font-semibold text-gray-500 dark:text-gray-400 text-right">Bets</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, i) => (
                  <tr key={i} className="border-b border-gray-50 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    {showDay    && <td className="py-2 pr-4 text-gray-700 dark:text-gray-300 tabular-nums">{row.day ? formatDayBucket(row.day) : '—'}</td>}
                    {showGame   && <td className="py-2 pr-4 text-gray-700 dark:text-gray-300 font-mono text-xs">{row.game_id ?? '—'}</td>}
                    {showPlayer && <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{row.display_name || row.player_id || '—'}</td>}
                    <td className="py-2 pr-4 text-right tabular-nums text-gray-700 dark:text-gray-300">{formatNum(row.turnover)}</td>
                    <td className="py-2 pr-4 text-right tabular-nums text-gray-700 dark:text-gray-300">{formatNum(row.winnings)}</td>
                    <td className={`py-2 pr-4 text-right tabular-nums font-semibold ${ggrColor(Number(row.ggr))}`}>{formatNum(row.ggr)}</td>
                    <td className="py-2 text-right tabular-nums text-gray-500 dark:text-gray-400">{formatNum(row.bet_count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {data && data.rows.length === 0 && (
        <Card>
          <p className="text-center text-gray-500 dark:text-gray-400 py-4">No data for the selected period.</p>
        </Card>
      )}
    </div>
  );
}
